/* Deck vs the field, run on our hardware (#96).
 *
 * The thin client for `POST /api/field`. The server half — the gate, the
 * cache, the thread pool, the cost ceiling — is in `server/api.mjs`; this is
 * the part that asks.
 *
 * ---------------------------------------------------------------------------
 * The free path is not going anywhere
 *
 * The Matchups tab still runs the whole field in a Web Worker on the user's
 * own machine, at the same game counts, against the same archetypes. This is
 * an *addition*: faster, cached, and a game count the browser does not offer.
 * Taking away something people already use would be a worse look than never
 * having had it, and "a gate on client-side code is theatre" cuts both ways —
 * the local engine costs nothing to run, so gating it would buy nothing.
 *
 * Which means every failure in here is survivable. A refusal, an outage, a
 * rate limit, a browser with no network: all of them fall back to the run the
 * user could always do. `kind` is what the caller switches on to decide what
 * to *say*, never whether the feature works at all.
 *
 * ---------------------------------------------------------------------------
 * Nothing here decides who is a supporter
 *
 * It sends a Discord token and reads a status code. `src/entitlement.js` is
 * server-side only and `test/entitlement.test.mjs` fails if anything under
 * `src/data`, `src/ui` or `src/engine` imports it — a client that could answer
 * the question could also lie about the answer, which is the whole reason
 * #96 was "move it server-side" rather than "add an if".
 */

/** Where the API lives. Same origin as the app, so no CORS and no config. */
export const FIELD_URL = '/api/field';

/**
 * Why a run did not happen. The caller shows a different thing for each, and
 * the difference between `signin` and `notMember` is the one that matters:
 * telling somebody who is paying that they are not a member is the single most
 * damaging thing this feature can do.
 *
 *   ok          it ran
 *   signin      no Discord token — sign in, or sign in again
 *   notMember   a definite no. Show what membership is for.
 *   limited     out of budget for now; `retryAfter` says when
 *   unavailable we could not find out or could not reach the server. NOT a no.
 *   rejected    the request was wrong — an illegal deck, a run too large
 */
export const FAILED = {
  SIGNIN: 'signin',
  NOT_MEMBER: 'notMember',
  LIMITED: 'limited',
  UNAVAILABLE: 'unavailable',
  REJECTED: 'rejected',
};

const KIND_BY_STATUS = {
  400: FAILED.REJECTED,
  401: FAILED.SIGNIN,
  403: FAILED.NOT_MEMBER,
  413: FAILED.REJECTED,
  429: FAILED.LIMITED,
};

/**
 * Play `deck` against the field, server-side.
 *
 * @param {object} o
 * @param {{leader: string, cards: Record<string, number>}} o.deck
 * @param {number} o.games        per matchup
 * @param {string|null} o.token   the Discord token from the session
 * @param {AbortSignal} [o.signal]
 * @param {typeof fetch} [o.fetchImpl]
 * @returns {Promise<{ok: true, rows: object[], overall: number, fieldWeighted: number,
 *                    field: number, games: number, cached: boolean, unavailable: string[]}
 *                  | {ok: false, kind: string, error: string, retryAfter: number|null}>}
 */
export async function runFieldOnServer({ deck, games, token, signal, fetchImpl = globalThis.fetch }) {
  /* Asked and answered without a round trip. The server says the same thing,
   * but there is no reason to spend a request finding out — and no reason for
   * a signed-out visitor's click to reach the network at all. */
  if (!token) {
    return fail(FAILED.SIGNIN, 'Sign in with Discord to run this on our servers.');
  }

  let res;
  try {
    res = await fetchImpl(FIELD_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify({ deck: { leader: deck.leader, cards: { ...deck.cards } }, games }),
      signal,
    });
  } catch (err) {
    /* An abort is the user changing their mind, not a failure to report. */
    if (err?.name === 'AbortError') return fail(FAILED.UNAVAILABLE, 'Cancelled.', null, true);
    return fail(FAILED.UNAVAILABLE, 'Could not reach the simulator.');
  }

  const retryAfter = Number(res.headers?.get?.('retry-after')) || null;

  let body = null;
  try { body = await res.json(); } catch { /* an error page, or a truncated response */ }

  if (res.ok && body?.rows) return { ok: true, ...body };

  /* Anything unrecognised is `unavailable` rather than a refusal. A proxy
   * returning 502, a captive portal returning HTML — neither is Discord
   * saying no, and neither should be reported as if it were. */
  const kind = KIND_BY_STATUS[res.status] ?? FAILED.UNAVAILABLE;
  const error = typeof body?.error === 'string' && body.error
    ? body.error
    : `The simulator answered ${res.status}.`;
  return fail(kind, error, retryAfter);
}

const fail = (kind, error, retryAfter = null, cancelled = false) =>
  ({ ok: false, kind, error, retryAfter, cancelled });
